import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, MapPin, Award, TrendingUp, DollarSign, Target, UserPlus, Building, Plus, Edit, Trash2, Search } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import {
  fetchConferences,
  fetchAttributionData,
  buildConferenceReport,
  formatMoney,
} from '../utils/conferenceAttribution';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';

const ConferencesReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  
  // State management
  const [conferences, setConferences] = useState([]);
  const [attribution, setAttribution] = useState({ accounts: [], purchases: [] });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingConference, setEditingConference] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newConference, setNewConference] = useState({
    name: '',
    startDate: '',
    endDate: '',
    location: '',
    type: 'Attendee', // Attendee, Sponsor, Speaker
    cost: '',
    notes: ''
  });

  const loadConferences = async () => {
    const conferencesData = await fetchConferences();
    setConferences(conferencesData);
    return conferencesData;
  };

  // Conferences plus the accounts and purchases their performance is derived from
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [, attributionData] = await Promise.all([
          loadConferences(),
          fetchAttributionData(),
        ]);
        setAttribution(attributionData);
      } catch (error) {
        console.error('Error fetching conference data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Leads, clients, MRR and revenue all come from matching accounts — only cost is entered by hand
  const report = useMemo(() => buildConferenceReport({
    conferences,
    accounts: attribution.accounts,
    purchases: attribution.purchases,
  }), [conferences, attribution]);

  const { rows, unspecified, totals } = report;
  const totalCost = totals.cost;
  const totalLeads = totals.leads;
  const totalClients = totals.clients;
  const totalRevenue = totals.revenue;
  const roi = totals.roi === null ? null : totals.roi.toFixed(1);

  // Prepare chart data
  const chartData = rows.map(row => ({
    name: row.name.length > 15 ? row.name.substring(0, 15) + '...' : row.name,
    fullName: row.name,
    cost: row.cost,
    leads: row.leads,
    clients: row.clients,
    revenue: row.revenue,
    roi: row.roi === null ? 0 : row.roi,
    date: row.date ? row.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : 'N/A',
    type: row.type
  }));

  // Monthly aggregated data
  const monthlyData = rows.reduce((acc, row) => {
    if (!row.date) return acc;
    const monthKey = row.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    if (!acc[monthKey]) {
      acc[monthKey] = { month: monthKey, cost: 0, leads: 0, clients: 0, revenue: 0, count: 0 };
    }
    acc[monthKey].cost += row.cost;
    acc[monthKey].leads += row.leads;
    acc[monthKey].clients += row.clients;
    acc[monthKey].revenue += row.revenue;
    acc[monthKey].count += 1;
    return acc;
  }, {});

  const monthlyChartData = Object.values(monthlyData).sort((a, b) => {
    const dateA = new Date(a.month + ' 01');
    const dateB = new Date(b.month + ' 01');
    return dateA - dateB;
  });

  // Conference type distribution
  const typeData = rows.reduce((acc, row) => {
    const type = row.type || 'Unknown';
    if (!acc[type]) {
      acc[type] = { name: type, value: 0, cost: 0, leads: 0, clients: 0 };
    }
    acc[type].value += 1;
    acc[type].cost += row.cost;
    acc[type].leads += row.leads;
    acc[type].clients += row.clients;
    return acc;
  }, {});

  const pieChartData = Object.values(typeData);

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Original docs by id, so the table can reach fields the report rows don't carry (notes)
  const conferencesById = useMemo(
    () => new Map(conferences.map(conf => [conf.id, conf])),
    [conferences]
  );

  // Filter conferences based on search query
  const filteredConferences = rows.filter(row => {
    const query = searchQuery.toLowerCase();
    if (!query) return true;
    const notes = conferencesById.get(row.id)?.notes || '';
    return (
      row.name?.toLowerCase().includes(query) ||
      row.location?.toLowerCase().includes(query) ||
      row.type?.toLowerCase().includes(query) ||
      notes.toLowerCase().includes(query)
    );
  });

  // Handle form submission. Leads, clients and revenue are never written here —
  // they are derived from accounts. Cost is the only number we can't infer.
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const conferenceData = {
        ...newConference,
        cost: parseFloat(newConference.cost) || 0,
        startDate: new Date(newConference.startDate),
        endDate: newConference.endDate ? new Date(newConference.endDate) : null,
        // Keep date for backward compatibility
        date: new Date(newConference.startDate),
        updatedAt: serverTimestamp()
      };

      if (editingConference) {
        // Older records live in `conferences-reports`; edit the document we actually read
        await updateDoc(doc(db, editingConference.collectionName || 'conferences', editingConference.id), conferenceData);
      } else {
        await addDoc(collection(db, 'conferences'), { ...conferenceData, createdAt: serverTimestamp() });
      }
      await loadConferences();

      setShowAddModal(false);
      setEditingConference(null);
      setNewConference({
        name: '',
        startDate: '',
        endDate: '',
        location: '',
        type: 'Attendee',
        cost: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error saving conference:', error);
    }
  };

  // Handle delete
  const handleDelete = async (conference) => {
    if (window.confirm('Are you sure you want to delete this conference?')) {
      try {
        await deleteDoc(doc(db, conference.collectionName || 'conferences', conference.id));
        setConferences(prev => prev.filter(conf => conf.id !== conference.id));
      } catch (error) {
        console.error('Error deleting conference:', error);
      }
    }
  };

  // Handle edit
  const handleEdit = (conference) => {
    setEditingConference(conference);
    setNewConference({
      name: conference.name || '',
      startDate: conference.startDate ? conference.startDate.toISOString().split('T')[0] : '',
      endDate: conference.endDate ? conference.endDate.toISOString().split('T')[0] : '',
      location: conference.location || '',
      type: conference.type || 'Attendee',
      cost: conference.cost != null ? conference.cost.toString() : '',
      notes: conference.notes || ''
    });
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen p-6 relative">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto">
          <BackButton />
          <div className="text-center mt-8">
            <BBALoader size={48} className="mx-auto mb-4" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading conferences...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <BackButton />
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Conferences Reports
          </h1>
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>

        {/* Metrics Cards — everything except cost is derived from accounts */}
        <p className="text-sm text-center text-gray-500 dark:text-gray-400 mb-4">
          Leads, clients, MRR and revenue are pulled live from accounts whose lead source matches a conference. Only cost is entered by hand.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-red-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cost</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatMoney(totalCost)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center">
              <UserPlus className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Leads</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalLeads}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Clients Acquired</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalClients}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{totals.conversionRate}% of leads</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatMoney(totalRevenue)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center">
              <Award className="h-8 w-8 text-emerald-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">MRR</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatMoney(totals.mrr)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-teal-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">ROI</p>
                <p className={`text-2xl font-bold ${roi === null ? 'text-gray-500' : roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {roi === null ? '—' : `${roi}%`}
                </p>
                {totals.costPerClient !== null && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{formatMoney(totals.costPerClient)} / client</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {unspecified.leads > 0 && (
          <div className="mb-8 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-200 text-center">
            {unspecified.leads} lead{unspecified.leads === 1 ? '' : 's'} ({unspecified.clients} client{unspecified.clients === 1 ? '' : 's'}, {formatMoney(unspecified.revenue)}) are tagged “Conference” without a specific event, so they are not counted against any conference below. Set their lead source to the conference on the lead to include them.
          </div>
        )}

        {/* Charts Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Conference Analytics</h2>
          
          {/* First Row - Monthly Trends and Conference Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Monthly Trends */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Monthly Trends</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'cost' || name === 'revenue' ? `$${value.toLocaleString()}` : value,
                      name === 'cost' ? 'Cost' : name === 'revenue' ? 'Revenue' : name === 'leads' ? 'Leads' : 'Clients'
                    ]}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="cost" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} />
                  <Area type="monotone" dataKey="revenue" stackId="2" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Conference Performance */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Conference Performance</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'cost' || name === 'revenue' ? `$${value.toLocaleString()}` : value,
                      name === 'cost' ? 'Cost' : name === 'revenue' ? 'Revenue' : name === 'leads' ? 'Leads' : 'Clients'
                    ]}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                  />
                  <Legend />
                  <Bar dataKey="leads" fill="#3b82f6" />
                  <Bar dataKey="clients" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Second Row - ROI Analysis and Conference Types */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* ROI Analysis */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">ROI Analysis</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      `${value.toFixed(1)}%`,
                      name === 'roi' ? 'ROI' : name
                    ]}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="roi" stroke="#f59e0b" strokeWidth={3} dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Conference Types Distribution */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Conference Types</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => [
                      `${value} conferences`,
                      props.payload.name
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Third Row - Cost vs Revenue and Lead Conversion */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Cost vs Revenue */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Cost vs Revenue</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      `$${value.toLocaleString()}`,
                      name === 'cost' ? 'Cost' : 'Revenue'
                    ]}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                  />
                  <Legend />
                  <Bar dataKey="cost" fill="#ef4444" />
                  <Bar dataKey="revenue" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Lead Conversion Rate */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">Lead Conversion Rate</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData.map(item => ({
                  ...item,
                  conversionRate: item.leads > 0 ? (item.clients / item.leads * 100) : 0
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      `${value.toFixed(1)}%`,
                      'Conversion Rate'
                    ]}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                  />
                  <Legend />
                  <Bar dataKey="conversionRate" fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Search and Add Conference */}
        <div className="mb-6 flex items-center gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search conferences..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Conference
          </button>
        </div>

        {/* Conferences Table */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Conference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date Range
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Leads
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Clients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    MRR
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    ROI
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredConferences.map((row) => {
                  const conference = conferencesById.get(row.id) || row;
                  // Only documents in `conferences` have a detail page
                  const hasDetailPage = row.collectionName === 'conferences';
                  return (
                  <tr
                    key={row.id}
                    onClick={() => hasDetailPage && navigate(`/crm/conferences-reports/${row.id}`)}
                    className={`${hasDetailPage ? 'cursor-pointer' : ''} hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {row.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {row.location}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {row.date ? row.date.toLocaleDateString() : 'N/A'}
                      {row.endDate && row.endDate.getTime() !== row.date?.getTime() && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          to {row.endDate.toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        row.type === 'Speaker' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                        row.type === 'Sponsor' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {row.type || 'Attendee'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatMoney(row.cost)}
                      {row.costPerLead !== null && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{formatMoney(row.costPerLead)} / lead</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {row.leads}
                      {row.recordedLeads > 0 && row.recordedLeads !== row.leads && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{row.recordedLeads} recorded</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {row.clients}
                      {row.recordedClients > 0 && row.recordedClients !== row.clients && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{row.recordedClients} recorded</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatMoney(row.mrr)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatMoney(row.revenue)}
                      {row.recordedRevenue > 0 && Math.round(row.recordedRevenue) !== Math.round(row.revenue) && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{formatMoney(row.recordedRevenue)} recorded</div>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${
                      row.roi === null ? 'text-gray-500 dark:text-gray-400' : row.roi >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {row.roi === null ? '—' : `${row.roi}%`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(conference);
                        }}
                        className="text-teal-600 hover:text-teal-900 mr-3"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(conference);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-800 w-96 shadow-xl rounded-2xl bg-white dark:bg-gray-900">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {editingConference ? 'Edit Conference' : 'Add Conference'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Conference Name
                    </label>
                    <input
                      type="text"
                      value={newConference.name}
                      onChange={(e) => setNewConference({...newConference, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={newConference.startDate}
                        onChange={(e) => setNewConference({...newConference, startDate: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={newConference.endDate}
                        onChange={(e) => setNewConference({...newConference, endDate: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      value={newConference.location}
                      onChange={(e) => setNewConference({...newConference, location: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type
                    </label>
                    <select
                      value={newConference.type}
                      onChange={(e) => setNewConference({...newConference, type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="Attendee">Attendee</option>
                      <option value="Sponsor">Sponsor</option>
                      <option value="Speaker">Speaker</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Cost ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={newConference.cost}
                      onChange={(e) => setNewConference({...newConference, cost: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Leads, clients, MRR and revenue are calculated from accounts whose lead source is this conference — set the lead source on the lead and it shows up here automatically.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={newConference.notes}
                      onChange={(e) => setNewConference({...newConference, notes: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        setEditingConference(null);
                        setNewConference({
                          name: '',
                          startDate: '',
                          endDate: '',
                          location: '',
                          type: 'Attendee',
                          cost: '',
                          notes: ''
                        });
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
                    >
                      {editingConference ? 'Update' : 'Add'} Conference
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConferencesReports;
